import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * Custom hook to toggle a member's active/inactive status.
 * This hook provides functionality to enable or disable a member account.
 * If the member is enabled, it becomes disabled and vice-versa.
 */
export const useDisableMember = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Toggles the member's status (active/inactive)
   * @param {string|number} memberId - The ID of the member whose status should be toggled
   * @param {() => void} [onSuccess] - Optional callback to run on success
   * @returns {Promise<void>}
   */
  const toggleMemberStatus = async (memberId, onSuccess) => {
    setLoading(true);
    setError("");
    setSuccess(false);
    setStatusMessage("");

    try {
      const response = await axiosInstance.patch(
        `${TENANT_DOMAIN}/school/admin/member/${memberId}/toggle-status`,
        {},
      );

      const responseData = response.data;

      setSuccess(true);
      setStatusMessage(responseData.message || "Status updated successfully");

      if (onSuccess) {
        onSuccess(responseData);
      }
    } catch (err) {
      setSuccess(false);
      if (err?.response?.status === 500 || err?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setError("");
    setSuccess(false);
    setStatusMessage("");
  };

  return {
    toggleMemberStatus,
    loading,
    error,
    success,
    statusMessage,
    resetState,
  };
};
